import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { queryClient } from "@/shared/services/queryClient";
import { normalizeError } from "@/shared/errors/normalizeError";
import {
  routeTemplatesApi,
  type CreateRouteTemplateRequest,
  type RouteTemplateListQuery,
  type UpdateRouteTemplateRequest,
} from "../services/routeTemplatesApi";

export const routeTemplateKeys = {
  all: ["routeTemplates"] as const,
  list: (query: RouteTemplateListQuery) => ["routeTemplates", "list", query] as const,
  detail: (id: string) => ["routeTemplates", "detail", id] as const,
};

export const useRouteTemplates = () =>
  useQuery({
    queryKey: routeTemplateKeys.all,
    queryFn: () => routeTemplatesApi.getAll(),
  });

export const useActiveRouteTemplates = () =>
  useQuery({
    queryKey: [...routeTemplateKeys.all, "active"],
    queryFn: () => routeTemplatesApi.getActive(),
  });

export const useRouteTemplateList = (query: RouteTemplateListQuery) =>
  useQuery({
    queryKey: routeTemplateKeys.list(query),
    queryFn: () => routeTemplatesApi.getPaged(query),
  });

export const useRouteTemplate = (id: string | null) =>
  useQuery({
    queryKey: id ? routeTemplateKeys.detail(id) : ["routeTemplates", "detail", "none"],
    queryFn: () => routeTemplatesApi.getById(id!),
    enabled: !!id,
  });

export const useCreateRouteTemplate = () =>
  useMutation({
    mutationFn: (data: CreateRouteTemplateRequest) => routeTemplatesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routeTemplateKeys.all });
      toast.success("Route template created.");
    },
    onError: (err: unknown) => {
      const envelope = normalizeError(err);
      if (envelope.fieldErrors?.length) {
        envelope.fieldErrors.forEach((fe) =>
          toast.error(`${fe.field}: ${fe.userMessage}`)
        );
      } else {
        toast.error(envelope.userMessage ?? "Failed to create route template.");
      }
    },
  });

export const useUpdateRouteTemplate = () =>
  useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRouteTemplateRequest }) =>
      routeTemplatesApi.update(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: routeTemplateKeys.all });
      queryClient.invalidateQueries({ queryKey: routeTemplateKeys.detail(variables.id) });
      toast.success("Route template updated.");
    },
    onError: (err: unknown) => {
      const envelope = normalizeError(err);
      if (envelope.fieldErrors?.length) {
        envelope.fieldErrors.forEach((fe) =>
          toast.error(`${fe.field}: ${fe.userMessage}`)
        );
      } else {
        toast.error(envelope.userMessage ?? "Failed to update route template.");
      }
    },
  });

export const useDeleteRouteTemplate = () =>
  useMutation({
    mutationFn: (id: string) => routeTemplatesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: routeTemplateKeys.all });
      toast.success("Route template removed.");
    },
    onError: (err: Error) => {
      toast.error(err.message ?? "Failed to remove route template.");
    },
  });
